<template>
  <div class="proofers-page mx-auto my-4">
    <v-card flat outlined>
      <v-card-title class="pb-1">
        {{ $t("settings.proofersSettings.title") }}
      </v-card-title>
      <v-card-subtitle class="pb-0">
        {{ $t("settings.proofersSettings.description") }}
      </v-card-subtitle>

      <v-tabs v-model="activeFeature" class="mt-2">
        <v-tab
          v-for="feature in features"
          :key="feature.key"
          :href="`#${feature.key}`"
        >
          {{ $t(feature.label) }}
        </v-tab>
      </v-tabs>

      <v-card-text class="pt-4">
        <div
          v-for="(stage, si) in currentChain"
          :key="activeFeature + '-stage-' + si"
          class="chain-stage"
        >
          <div class="d-flex align-center mb-2">
            <v-chip x-small color="primary" class="mr-2">{{ si + 1 }}</v-chip>
            <v-text-field
              v-model="stage.name"
              :label="$t('settings.proofersSettings.stageName')"
              :disabled="isReadOnlyUser"
              outlined
              dense
              hide-details
            />
            <v-btn
              v-if="!isReadOnlyUser"
              icon
              class="ml-1"
              :disabled="si === 0"
              :aria-label="$t('settings.proofersSettings.moveUp')"
              @click="moveStage(si, -1)"
            >
              <v-icon>mdi-arrow-up</v-icon>
            </v-btn>
            <v-btn
              v-if="!isReadOnlyUser"
              icon
              :disabled="si === currentChain.length - 1"
              :aria-label="$t('settings.proofersSettings.moveDown')"
              @click="moveStage(si, 1)"
            >
              <v-icon>mdi-arrow-down</v-icon>
            </v-btn>
            <v-btn
              v-if="!isReadOnlyUser"
              icon
              :aria-label="$t('general.delete')"
              @click="removeStage(si)"
            >
              <v-icon color="error">mdi-close</v-icon>
            </v-btn>
          </div>

          <v-autocomplete
            v-model="stage.members"
            :items="users"
            :label="$t('settings.proofersSettings.usersLabel')"
            :loading="loading"
            :disabled="isReadOnlyUser"
            item-text="name"
            item-value="_id"
            multiple
            chips
            small-chips
            deletable-chips
            outlined
            dense
            hide-details
          />

          <div class="text-caption grey--text mt-1">
            {{ $t("settings.proofersSettings.stageHint") }}
          </div>

          <v-icon v-if="si < currentChain.length - 1" class="chain-arrow">
            mdi-arrow-down
          </v-icon>
        </div>

        <div v-if="!currentChain.length" class="grey--text mb-2">
          {{ $t("settings.proofersSettings.emptyChain") }}
        </div>

        <v-btn
          v-if="!isReadOnlyUser"
          text
          small
          color="primary"
          class="mt-2"
          @click="addStage"
        >
          <v-icon left small>mdi-plus</v-icon>
          {{ $t("settings.proofersSettings.addStage") }}
        </v-btn>
      </v-card-text>
    </v-card>

    <div class="d-flex justify-end mt-4">
      <v-btn
        v-if="!isReadOnlyUser"
        color="primary"
        outlined
        :loading="saving"
        @click="save"
      >
        {{ $t("general.save") }}
      </v-btn>
    </div>
  </div>
</template>

<script>
const FEATURES = [
  { key: "riskAssessment", label: "sidebar.riskAssessment" },
  { key: "dangerousGood", label: "sidebar.dangerousGoods" },
  { key: "operatingInstruction", label: "sidebar.operatingInstructions" },
  { key: "medkitlog", label: "sidebar.medkitlogs" },
  { key: "incidentReport", label: "sidebar.incidentReports" },
  { key: "environmentalReport", label: "sidebar.environmentalReports" },
];

export default {
  name: "proofersSettings",
  data: () => ({
    features: FEATURES,
    activeFeature: "riskAssessment",
    chains: {},
    proofers: { riskAssessment: [], dangerousGood: [] },
    users: [],
    loading: false,
    saving: false,
  }),
  computed: {
    currentChain() {
      return this.chains[this.activeFeature] || [];
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    addStage() {
      this.currentChain.push({ name: "", members: [] });
    },
    removeStage(index) {
      this.currentChain.splice(index, 1);
    },
    moveStage(index, delta) {
      const target = index + delta;
      if (target < 0 || target >= this.currentChain.length) return;
      const [stage] = this.currentChain.splice(index, 1);
      this.currentChain.splice(target, 0, stage);
    },
    async initialize() {
      this.loading = true;
      try {
        const [proofers, usersData] = await Promise.all([
          this.$store.dispatch("getProofers"),
          this.$store.dispatch("getUsersData"),
        ]);
        this.users = usersData || [];

        const pv = (proofers && proofers.value) || {};
        this.proofers = {
          riskAssessment: [...(pv.riskAssessment || [])],
          dangerousGood: [...(pv.dangerousGood || [])],
        };

        const chains = {};
        for (const feature of FEATURES) {
          chains[feature.key] = (pv[`${feature.key}Chain`] || []).map(
            (stage) => ({
              name: stage.name || "",
              members: [...(stage.members || [])],
            })
          );
        }
        this.chains = chains;
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        const payload = {
          riskAssessment: (this.proofers.riskAssessment || []).filter(Boolean),
          dangerousGood: (this.proofers.dangerousGood || []).filter(Boolean),
        };

        for (const feature of FEATURES) {
          payload[`${feature.key}Chain`] = (this.chains[feature.key] || [])
            .map((stage) => ({
              name: (stage.name || "").trim(),
              members: (stage.members || []).filter(Boolean),
            }))
            .filter((stage) => stage.name && stage.members.length);
        }

        await this.$store.dispatch("setProofers", payload);
        this.$store.commit(
          "CREATE_SNACKBAR",
          this.$t("general.savedSuccessfully")
        );
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.proofers-page {
  max-width: 1200px;
  padding-bottom: 120px;
}
.chain-stage {
  margin-bottom: 20px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  position: relative;
}
.chain-arrow {
  position: absolute;
  bottom: -22px;
  left: 50%;
  transform: translateX(-50%);
  color: #9e9e9e;
}
</style>
